import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Button, FormControl, Grid, InputLabel, MenuItem, Popover, Select, Switch, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { Edit as EditIcon } from '@mui/icons-material';
import { useGetChartsQuery } from '../features/api/rest/apiChartsSlice';
import WordCloudComponent from './WordCloudComponent';

function WordCloud({ leftSearchParamsName = '', rightSearchParamsName = 'split' }) {
  const { t } = useTranslation();
  const { ean } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const componentLeftSearchParamsName = leftSearchParamsName ? `${leftSearchParamsName}_` : '';
  const componentRightSearchParamsName = rightSearchParamsName ? `${rightSearchParamsName}_` : 'split_';
  const section = searchParams.get(`${componentLeftSearchParamsName}section`) || 'all';
  const sentiment = searchParams.get(`${componentLeftSearchParamsName}sentiment`) || 'all';
  const split = (searchParams.get('split') || 'false') === 'true';
  const splitSection = searchParams.get(`${componentRightSearchParamsName}section`) || 'all';
  const splitSentiment = searchParams.get(`${componentRightSearchParamsName}sentiment`) || 'all';
  const { tagcloud, isLoading, isError } = useGetChartsQuery(
    { ean },
    {
      selectFromResult: (results) => ({
        ...results,
        tagcloud: results?.data,
      }),
    }
  );
  const [attributeLeft, setAttributeLeft] = useState('');
  const [attributeRight, setAttributeRight] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [isSplitted, setIsSplitted] = useState(split);
  const [attributeValues, setAttributeValues] = useState({
    left: {
      section,
      sentiment,
    },
    right: {
      section: splitSection,
      sentiment: splitSentiment,
    },
  });

  useEffect(() => {
    setIsSplitted(split);
  }, [split]);

  useEffect(() => {
    const sectionParam = attributeValues.left.section === 'all' ? '' : `section:${attributeValues.left.section}`;
    const sentimentParam = attributeValues.left.sentiment === 'all' ? '' : `sentiment:${attributeValues.left.sentiment}`;
    sectionParam && sentimentParam ? setAttributeLeft(`${sectionParam}&${sentimentParam}`) : setAttributeLeft(sectionParam + sentimentParam);
    sectionParam ? searchParams.set(`${componentLeftSearchParamsName}section`, attributeValues.left.section) : searchParams.delete('section');
    sentimentParam ? searchParams.set(`${componentLeftSearchParamsName}sentiment`, attributeValues.left.sentiment) : searchParams.delete('sentiment');
    navigate(`${pathname}?${searchParams.toString()}`);
  }, [attributeValues.left]);

  useEffect(() => {
    const sectionParam = attributeValues.right.section === 'all' ? '' : `section:${attributeValues.right.section}`;
    const sentimentParam = attributeValues.right.sentiment === 'all' ? '' : `sentiment:${attributeValues.right.sentiment}`;
    sectionParam && sentimentParam ? setAttributeRight(`${sectionParam}&${sentimentParam}`) : setAttributeRight(sectionParam + sentimentParam);
    sectionParam ? searchParams.set(`${componentRightSearchParamsName}section`, attributeValues.right.section) : searchParams.delete('split_section');
    sentimentParam ? searchParams.set(`${componentRightSearchParamsName}sentiment`, attributeValues.right.sentiment) : searchParams.delete('split_sentiment');
    navigate(`${pathname}?${searchParams.toString()}`);
  }, [attributeValues.right]);

  const handleSelectChange = (event, splitParam) => {
    setAttributeValues({
      ...attributeValues,
      [splitParam]: {
        ...attributeValues[splitParam],
        [event.target.name]: event.target.value,
      },
    });
  };

  const handleSplitChange = (event) => {
    setIsSplitted(event.target.checked);
    event.target.checked ? searchParams.set('split', event.target.checked) : searchParams.delete('split');
    navigate(`${pathname}?${searchParams.toString()}`);
  };

  if (isError || (!isLoading && tagcloud === undefined)) {
    return (
      <Typography variant="h5" sx={{ mt: 3 }}>
        {t('charts.wordcloud.noData')}
      </Typography>
    );
  }

  return (
    <Box pt={3}>
      <Button variant="contained" startIcon={<EditIcon />} onClick={(e) => setAnchorEl(e.currentTarget)}>
        {t('charts.wordcloud.filter')}
      </Button>
      <Grid container display="flex" justifyContent="center" mt={2} columnGap={10} maxHeight="200vh">
        <Grid item minWidth="0px" height="90vh">
          <WordCloudComponent ean={ean} attribute={attributeLeft} />
        </Grid>
        {isSplitted && (
          <Grid item minWidth="0px" height="90vh">
            <WordCloudComponent ean={ean} attribute={attributeRight} searchParamsName="split" />
          </Grid>
        )}
      </Grid>

      <Popover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={() => setAnchorEl(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}>
        <Grid container display="flex" alignItems="center" flexWrap="no-wrap" columnGap={3} rowGap={2} p={2}>
          <Grid item>
            <FormControl>
              <InputLabel>Section</InputLabel>
              <Select name="section" label="Section" value={attributeValues.left.section} onChange={(e) => handleSelectChange(e, 'left')} sx={{ mr: 1, width: 120 }}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="prior">Prior</MenuItem>
                <MenuItem value="current">Current</MenuItem>
                <MenuItem value="future">Future</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Sentiment</InputLabel>
              <Select name="sentiment" label="Sentiment" value={attributeValues.left.sentiment} onChange={(e) => handleSelectChange(e, 'left')} sx={{ width: 120 }}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="positive">Positive</MenuItem>
                <MenuItem value="negative">Negative</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
        <Grid container alignItems="center" sx={{ ml: 2, display: 'flex', justifyContent: 'left' }}>
          <Grid item>
            <Switch checked={isSplitted} onChange={handleSplitChange} />
          </Grid>
          <Grid item>
            <Typography>{t('charts.wordcloud.splitscreen')}</Typography>
          </Grid>
        </Grid>
        <Grid container display="flex" alignItems="center" flexWrap="no-wrap" columnGap={3} rowGap={2} p={2}>
          <Grid item>
            <FormControl>
              <InputLabel>Section</InputLabel>
              <Select name="section" label="Section" value={attributeValues.right.section} onChange={(e) => handleSelectChange(e, 'right')} sx={{ mr: 1, width: 120 }} disabled={!isSplitted}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="prior">Prior</MenuItem>
                <MenuItem value="current">Current</MenuItem>
                <MenuItem value="future">Future</MenuItem>
              </Select>
            </FormControl>
            <FormControl>
              <InputLabel>Sentiment</InputLabel>
              <Select name="sentiment" label="Sentiment" value={attributeValues.right.sentiment} onChange={(e) => handleSelectChange(e, 'right')} sx={{ width: 120 }} disabled={!isSplitted}>
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="positive">Positive</MenuItem>
                <MenuItem value="negative">Negative</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Popover>
    </Box>
  );
}

export default WordCloud;

WordCloud.propTypes = {
  leftSearchParamsName: PropTypes.string,
  rightSearchParamsName: PropTypes.string,
};
